const COUNTERS = [
  'bytesReceived', 'packetsReceived', 'packetsLost', 'framesReceived', 'framesDecoded',
  'framesDropped', 'keyFramesDecoded', 'nackCount', 'pliCount', 'freezeCount',
  'totalFreezesDuration', 'jitterBufferDelay', 'jitterBufferEmittedCount',
  'jitterBufferTargetDelay', 'jitterBufferMinimumDelay', 'totalDecodeTime', 'totalProcessingDelay',
  'totalAssemblyTime', 'framesAssembledFromMultiplePackets', 'retransmittedPacketsReceived'
]
const GAUGES = ['timestamp', 'ssrc', 'jitter', 'framesPerSecond', 'frameWidth', 'frameHeight']

function numbers(source, fields) {
  const result = {}
  for (const field of fields) {
    if (typeof source?.[field] === 'number' && Number.isFinite(source[field])) result[field] = source[field]
  }
  return result
}

export function intervalStats(current, previous) {
  const delta = {}
  const intervalMs = previous ? current.timestamp - previous.timestamp : null
  for (const key of COUNTERS) {
    const a = current[key]
    const b = previous?.[key]
    delta[key] = intervalMs > 0 && Number.isFinite(a) && Number.isFinite(b) && a >= b ? a - b : null
  }
  const average = (total, count) => delta[total] !== null && delta[count] > 0
    ? delta[total] * 1000 / delta[count] : null
  return {
    intervalMs: intervalMs > 0 ? intervalMs : null,
    delta,
    jitterBufferMs: average('jitterBufferDelay', 'jitterBufferEmittedCount'),
    jitterTargetMs: average('jitterBufferTargetDelay', 'jitterBufferEmittedCount'),
    jitterMinimumMs: average('jitterBufferMinimumDelay', 'jitterBufferEmittedCount'),
    decodeMs: average('totalDecodeTime', 'framesDecoded'),
    assemblyMs: average('totalAssemblyTime', 'framesAssembledFromMultiplePackets'),
    // Processing includes buffering/decoding; these metrics must not be added together.
    processingMs: average('totalProcessingDelay', 'framesDecoded')
  }
}

export function createMediaDiagnostics({ env = window, root, sdk, profile, onSummary = () => {}, capacity = 20000 }) {
  const records = new Array(Math.max(32, capacity))
  const peers = []
  const restores = []
  const frameWatches = new Map()
  let count = 0
  let written = 0
  let stopped = false
  let gesture = 0
  let control = null
  let lastMove = -Infinity
  let lastSummary = -Infinity
  let watchUntil = 0
  let latest = {}
  const clock = () => env.performance.now()

  function record(event, fields = {}) {
    if (stopped) return
    records[written % records.length] = { event, wallMs: Date.now(), monoMs: clock(), ...fields }
    written++
    count = Math.min(count + 1, records.length)
  }

  function hook(target, name, wrap) {
    if (typeof target?.[name] !== 'function') return false
    const original = target[name]
    const wrapped = wrap(original)
    target[name] = wrapped
    restores.push(() => { if (target[name] === wrapped) target[name] = original })
    return true
  }

  function watchFrames() {
    watchUntil = clock() + 1500
    root.querySelectorAll('video').forEach((video, videoIndex) => {
      if (frameWatches.has(video) || typeof video.requestVideoFrameCallback !== 'function') return
      const callback = (now, metadata) => {
        frameWatches.delete(video)
        if (stopped) return
        record('video-frame', {
          gesture, videoIndex, callbackMonoMs: now, responseToInputUnverified: true,
          ...numbers(metadata, ['mediaTime', 'presentationTime', 'expectedDisplayTime',
            'processingDuration', 'receiveTime', 'captureTime', 'rtpTimestamp', 'presentedFrames', 'width', 'height'])
        })
        if (clock() < watchUntil) frameWatches.set(video, video.requestVideoFrameCallback(callback))
      }
      frameWatches.set(video, video.requestVideoFrameCallback(callback))
    })
  }

  const inputEvents = env.PointerEvent ? ['pointerdown', 'pointerup', 'pointercancel'] : ['mousedown', 'mouseup']
  function observeInput(event) {
    if (event.type.endsWith('down')) gesture++
    record('input', { gesture, phase: event.type, eventTimestamp: event.timeStamp })
    watchFrames()
  }
  for (const name of inputEvents) {
    root.addEventListener(name, observeInput, { capture: true, passive: true })
    restores.push(() => root.removeEventListener(name, observeInput, true))
  }

  hook(sdk, 'sendTouchMulti', (original) => function (type, points) {
    const previous = control
    const move = type === sdk.TOUCH_TYPE?.TOUCH_MOVE
    const sample = !move || clock() - lastMove >= 100
    if (move && sample) lastMove = clock()
    control = sample ? { gesture, touchType: type, points: Array.isArray(points) ? points.length : null } : null
    if (control) record('sdk-touch', control)
    try { return original.apply(this, arguments) } finally { control = previous }
  })
  hook(sdk, 'send', (original) => function () {
    if (control) record('sdk-send', control)
    return original.apply(this, arguments)
  })
  const socketHooked = hook(env.WebSocket?.prototype, 'send', (original) => function (data) {
    if (!control) return original.apply(this, arguments)
    const started = clock()
    const bufferedBefore = this.bufferedAmount
    let failed = false
    try { return original.apply(this, arguments) } catch (error) { failed = true; throw error } finally {
      record('websocket-enqueue', {
        ...control, sendStartedMonoMs: started, callDurationMs: clock() - started,
        bufferedBefore, bufferedAfter: this.bufferedAmount, readyState: this.readyState,
        bytes: typeof data === 'string' ? null : (data?.byteLength ?? data?.size ?? null),
        failed, deliveryUnverified: true
      })
    }
  })
  const peerHooked = hook(env.RTCPeerConnection?.prototype, 'setRemoteDescription', (original) => function () {
    if (!peers.some((peer) => peer.pc === this)) {
      peers.push({ pc: this, id: peers.length + 1, busy: false, previous: new Map() })
      record('peer-observed', { peer: peers.length })
    }
    return original.apply(this, arguments)
  })

  function consumeReport(peer, report, started) {
    const entries = Array.from(report.values())
    const transport = entries.find((item) => item.type === 'transport' && item.selectedCandidatePairId)
    const pair = transport ? report.get(transport.selectedCandidatePairId)
      : entries.find((item) => item.type === 'candidate-pair' && item.state === 'succeeded' && item.nominated)
    if (pair) {
      const local = report.get(pair.localCandidateId)
      const remote = report.get(pair.remoteCandidateId)
      const path = {
        peer: peer.id, selection: transport ? 'selected' : 'nominated-inferred',
        localProtocol: local?.protocol ?? null, remoteProtocol: remote?.protocol ?? null,
        localCandidateType: local?.candidateType ?? null, remoteCandidateType: remote?.candidateType ?? null,
        ...numbers(pair, ['currentRoundTripTime', 'availableOutgoingBitrate', 'bytesReceived', 'bytesSent'])
      }
      record('ice-path', path)
      latest = { ...latest, protocol: path.localProtocol, remoteProtocol: path.remoteProtocol, selection: path.selection }
    }
    for (const item of entries) {
      if (item.type !== 'inbound-rtp' || (item.kind || item.mediaType) !== 'video') continue
      const raw = numbers(item, [...COUNTERS, ...GAUGES])
      const interval = intervalStats(raw, peer.previous.get(item.id))
      peer.previous.set(item.id, raw)
      record('inbound-video', { peer: peer.id, statsCallMs: clock() - started, raw, ...interval })
      latest = { ...latest, ...interval, width: raw.frameWidth, height: raw.frameHeight }
    }
  }

  function sample() {
    if (stopped) return
    for (const peer of peers) {
      if (peer.busy || peer.pc.connectionState === 'closed') continue
      peer.busy = true
      const started = clock()
      Promise.resolve().then(() => stopped ? null : peer.pc.getStats()).then((report) => {
        if (!stopped && report) consumeReport(peer, report, started)
      }).catch(() => { record('stats-unavailable', { peer: peer.id }) }).finally(() => { peer.busy = false })
    }
    if (clock() - lastSummary >= 1000) {
      lastSummary = clock()
      const reportedWebRtc = typeof sdk.videoinfo?.isWebRtc === 'boolean' ? sdk.videoinfo.isWebRtc : null
      record('sdk-state', { requestedWebRtc: sdk.config.isWebRtc === true, reportedWebRtc, peers: peers.length })
      try { onSummary({ ...latest, reportedWebRtc, peers: peers.length, records: count, overwritten: written - count }) } catch { /* Diagnostics must not interrupt playback. */ }
    }
  }

  function setProfile(value) {
    profile = value
    record('profile', { bitrateKbps: value.bitrateKbps, minFps: value.minFps, maxFps: value.maxFps })
  }
  record('diagnostics-start', { version: 'browser-rtp-20261024-v1', samplePeriodMs: 100,
    requestedWebRtc: sdk.config.isWebRtc === true, peerHooked, socketHooked, capacity: records.length })
  setProfile(profile)
  const timer = env.setInterval(sample, 100)

  return {
    setProfile,
    clear() {
      records.fill(undefined)
      count = written = 0
      record('capture-reset', { version: 'browser-rtp-20261024-v1', samplePeriodMs: 100,
        requestedWebRtc: sdk.config.isWebRtc === true, peers: peers.length })
      setProfile(profile)
    },
    exportJsonl() {
      const output = [{ event: 'export-info', version: 'browser-rtp-20261024-v1', count, overwritten: written - count,
        stopped, excludes: ['credentials', 'SDP', 'addresses', 'control-payloads'],
        nextVideoFrameIsNotProofOfInputResponse: true }]
      for (let i = written - count; i < written; i++) output.push(records[i % records.length])
      return output.map((item) => JSON.stringify(item)).join('\n') + '\n'
    },
    stop() {
      if (stopped) return
      record('diagnostics-stop')
      stopped = true
      env.clearInterval(timer)
      restores.reverse().forEach((restore) => restore())
      frameWatches.forEach((id, video) => video.cancelVideoFrameCallback?.(id))
      frameWatches.clear()
      peers.length = 0
    }
  }
}
